import { pageContext } from 'ctm-util-functions/lib/page_context';
import { get } from 'ctm-util-functions/lib/get';
import Immutable from 'immutable';
import qsParser from 'querystring';

/**
 * Selector to get the current breakpoint.
 * @param {Immutable.Map} state - The full application state
 * @returns {Immutable.Map} - The current breakpoint
 */
// @todo: This function uses ctm-redux-responsive which doesn't return an immutable object currently as it uses get, needs to be fixed.
export const browserMediaTypeSelector = (state) => {
    return state.get('browser').mediaType;
};

/**
 * Selector to get the current invalid elements.
 * @param {Immutable.Map} state - The full application state
 * @returns {Immutable.Map} - The current invalid elements
 */
export const invalidElementsSelector = (state) => {
    return state.getIn(['vertical', 'quote', 'validation', 'invalidElements']);
};

/**
 * Selector to get the current query params
 * @param {Immutable.Map} state - The full application state
 * @returns {Immutable.Map} - The current query params
 */
export const locationQuerySelector = (state) => {
    const search = state.getIn(['page', 'location', 'search']);
    return Immutable.Map(search ? qsParser.parse(search.substr(1)) || {} : {});
};

/**
 * Selector to get the experimentId from state used for a/b testing.
 * @param  {Immutable.Map} state - The application Redux store.
 * @returns {String} - The experimentId state value.
 */
export const experimentIdSelector = (state) => {
    return state.getIn(['page', 'experimentId']);
};

/**
 * Selector to get the page name state.
 * @param {Immutable.Map} state - The full application state
 * @returns {String} - The current page name
 */
export const pageNameSelector = (state) => {
    return state.getIn(['page', 'name']);
};

/**
 * Selector to get the showPleaseWait status
 * @param {Immutable.Map} state - The full application state
 * @returns {Boolean} - The showPleaseWait status
 */
export const pageShowPleaseWaitSelector = (state) => {
    return state.getIn(['page', 'showPleaseWait']);
};

/**
 * Selector to get the serverTime in UTC+10 (AEST)
 * @param {Immutable.Map} state - The full application state
 * @returns {String} - The current serverTime
 */
export const pageServerTimeSelector = (state) => {
    return state.getIn(['page', 'serverTime']);
};

/**
 * Selector to get the current selectedProductId
 * @param {Immutable.Map} state - The full application state
 * @returns {String} - The current selectedProductId
 */
export const selectedProductIdSelector = (state) => {
    return state.getIn(['vertical', 'quote', 'form', 'data', 'selectedProductId']);
};

/**
 * Selector to get the form helpers state
 * @param {Immutable.Map} state - The full application state
 * @returns {Immutable.Map} - The form helpers state
 */
export const formHelpersSelector = (state) => {
    return state.getIn(['vertical', 'quote', 'form', 'helpers']);
};

/**
 * Selector to get the validation state
 * @param {Immutable.Map} state - The full application state
 * @param {String} stateKey - the parent state node for the form, e.g. vertical.quote, vertical.callMeBack
 * @returns {Immutable.Map} - The validation state
 */
export const validationSelector = (state, stateKey = 'vertical.quote') => {
    return state.getIn([...stateKey.split('.'), 'validation']);
};

/**
 * Determines if the form on the journey page specified is valid.
 * @param {Immutable.Map} state - The full application state
 * @param {String} journeyPageName - Name of the journey page to check for validity
 * @returns {Boolean} - true if the form on journeyPageName is valid, false otherwise.
 */
export const isJourneyPageValid = (state, journeyPageName) => {
    return get(validationSelector(state), `pages.${journeyPageName}`);
};

/**
 * @todo: had to change this so tracking is using the correct name, currently only used for tracking,
 * @todo: not sure if this still belongs here as it doesn't need to extract from the state
 * Selector to get the vertical name
 * @returns {String} - The vertical name
 */
export const verticalNameSelector = () => {
    const settings = pageContext.getProps().models.pageSchemaModel.getSettings();
    return get(settings, 'tracking.vertical', null);
};

/**
 * Selector to get apiResponses from the state
 * @param {Immutable.Map} state - The full application state
 * @returns {Immutable.Map} - apiResponses state
 */
export const apiResponsesSelector = (state) => {
    return state.getIn(['vertical', 'quote', 'apiResponses']) || Immutable.Map();
};

/**
 * Selector to get the splitTrafficId from state used for a/b testing.
 * @param  {Immutable.Map} state - The application Redux store.
 * @returns {String} - The splitTrafficId state value.
 */
export const splitTrafficIdSelector = (state) => {
    return state.getIn(['page', 'splitTrafficId']);
};

/**
 * Retrieves the value of a feature flag from the server configuration.
 *
 * @param {string} featFlag - The name of the feature flag to retrieve.
 * @return {*} The value of the feature flag, or undefined if it does not exist.
 */
export const getFeatureFlag = (featFlag) => {
    const featureFlag = pageContext.getProps().pageState.serverConfig.get(featFlag);
    return featureFlag;
};

/**
 * Selector to get all brandable content: links, images, medias, and copy
 * @param {Immutable.Map} state - The full application state
 * @returns {Immutable.Map} - The content from enterprise branding service
 *
 * @see https://dev.azure.com/ctmaus/CTM/_git/ctm-enterprise-branding for schema
 */
export const verticalContentSelector = (state) => {
    return state.getIn(['vertical', 'content']);
};
